<template>
    <DataTable
        ref="rolesTable"
        :value="roles"
        v-if="roles"
        selectionMode="single"
        @row-click="
            (event: DataTableRowClickEvent) => {
                const rowLink = `role-link-${event.data.identity}`;
                rolesTable.$el.querySelector(`#${rowLink}`)?.click();
            }
        "
    >
        <template #empty>
            <div>No roles</div>
        </template>
        <template #header>
            <div style="display: flex; justify-content: space-between">
                <h2>Roles</h2>
                <Button
                    v-if="isEditing"
                    class="p-button-secondary"
                    icon="pi pi-plus"
                    label="Add role"
                    severity="secondary"
                    size="small"
                    text
                    @click="openEditRole()"
                />
            </div>
        </template>
        <Column field="name" header="Name" class="name-col">
            <template #body="{ data, field }">
                <RouterLink
                    :id="`role-link-${data.identity}`"
                    class="role-link"
                    :to="{
                        name: 'rolePage',
                        params: {
                            orgName: route.params.orgName,
                            stekName: route.params.stekName,
                            roleId: data.identity,
                        },
                    }"
                >
                    <div>
                        {{ data[field] }}
                    </div>
                </RouterLink>
            </template>
        </Column>
        <Column field="short_description" header="Description">
            <template #body="slotProps">
                <template v-if="slotProps.data.short_description">
                    {{ slotProps.data.short_description }}
                </template>
                <span
                    v-else
                    v-tooltip.top="'A description is not (yet) available'"
                >
                    ...
                </span>
            </template>
        </Column>
        <Column field="" header="" class="buttons-col">
            <template #body="{ index, data }" v-if="isEditing">
                <Button
                    severity="secondary"
                    type="button"
                    text
                    rounded
                    icon="pi pi-pencil"
                    :loading="isLoading"
                    @click="
                        () => {
                            openEditRole(data);
                        }
                    "
                />
                <Button
                    severity="danger"
                    type="button"
                    text
                    rounded
                    icon="pi pi-trash"
                    :loading="isLoading"
                    @click="
                        (e: MouseEvent) => {
                            // Using stopPropagation will break confirm: https://github.com/primefaces/primevue/issues/5706
                            deleteRoleEntity(e, index);
                        }
                    "
                />
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import type { DataTableRowClickEvent } from "primevue/datatable";
import { useRoute } from "vue-router";
import { useConfirm } from "primevue/useconfirm";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import { useRolesStore, useRolesManager } from "@/stores/roles-store";
import { type Role } from "@/composables/role-manager";
import { ref } from "vue";

const { isLoading } = useRolesManager();
const { deleteRole } = useRolesStore();

const rolesTable = ref();

const props = defineProps<{
    isEditing: boolean;
    roles: Role[] | undefined;
}>();

const route = useRoute();
const confirm = useConfirm();
const { open: openEditRole } = useEditRoleDialog();

const deleteRoleEntity = async (event: Event, index: number) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: `
            After deletion of the '${props.roles![index].name}' you
            cannot use it anymore. Are you sure?
        `,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            await deleteRole(props.roles![index].identity);
        },
    });
};
</script>

<style scoped lang="scss">
.role-link {
    text-decoration: none;
}

:deep(.p-datatable-thead) > tr > th,
:deep(.p-datatable-tbody) > tr > td {
    &.buttons-col {
        min-width: 50px;
        text-align: right;
    }
    &.name-col {
        width: 30rem;
    }
}
:deep(.p-datatable-tbody) > tr > td.buttons-col {
    padding-top: 0;
    padding-bottom: 0;
}
</style>
